import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { SelectOption } from '../../interfaces/select-option.interface';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
 

  constructor() { }

  public getNextDateOfDay(day: number) {
    const today = new Date(); // Get the current date
    const currentDay = today.getDay(); // Current day of the week (0 for Sunday, 6 for Saturday)
    const daysUntilDesiredDay = day - currentDay; // Difference between desired and current day
    const nextDay = new Date(today); // Clone the current date

    if (daysUntilDesiredDay >= 0) {
      // Desired day is yet to come this week (or is today)
      nextDay.setDate(today.getDate() + daysUntilDesiredDay);
    } else {
      // Desired day has already passed, move to the next week
      nextDay.setDate(today.getDate() + (7 + daysUntilDesiredDay));
    }

    return nextDay;
  }

  stringToEnum<T>(str: string, enumeration: T): T[keyof T] | undefined {
    return Object.values(enumeration).find((value: string) => value === str) as
      | T[keyof T]
      | undefined;
  }

  getCurrentTime(): string {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  compareDatesString(date1: Date, date2: Date) {
    return date1.toDateString() == date2.toDateString();
  }

  getLocaleDate(date: string | Date): Date {
    const dateStr = new Date(date).toLocaleString('he-IL');
    return new Date(dateStr);
  }

  updateIfFormDirty(form: FormGroup, newValues: any) {
    const currentValues = form.value;

    for (let key in newValues) {
      if (newValues[key] !== currentValues[key]) {
        form.markAsDirty();
        break;
      }
    }
  }

  decodeUnicode(str) {
    // Decode Unicode escape sequences like \uXXXX
    return (
      str
        .replace(/\\u([\dA-Fa-f]{4})/g, function (match, code) {
          return String.fromCharCode(parseInt(code, 16));
        })
        // Handle newline characters properly
        .replace(/\n/g, '<br>')
        .replace(/\\r/g, '<br>')
    );
  }

  convertFirstLatterCapital(string: string) {
    const englishRegex = /^[A-Za-z\s]*$/;
    if (!englishRegex.test(string)) {
      return string;
    }
    return string[0].toUpperCase() + string.slice(1);
  }


  static lightenColor(hexColor: string, percentage: number): string {
    const hexToRgb = (hex: string): [number, number, number] => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return [r, g, b];
    };

    // Convert RGB back to hex
    const rgbToHex = (r: number, g: number, b: number): string =>
      `#${[r, g, b]
        .map((x) => x.toString(16).padStart(2, "0"))
        .join("")}`;

    // Lighten color by a factor
    const lighten = (r: number, g: number, b: number, factor: number): [number, number, number] => {
      return [
        Math.min(255, Math.round(r + (255 - r) * factor)),
        Math.min(255, Math.round(g + (255 - g) * factor)),
        Math.min(255, Math.round(b + (255 - b) * factor)),
      ];
    };

    // Parse input and lighten based on index
    const [r, g, b] = hexToRgb(hexColor);
    const factor = percentage; // Incrementally increase lightness per index
    const [newR, newG, newB] = lighten(r, g, b, factor);

    return rgbToHex(newR, newG, newB);
  };

  createUTCDate() {
    // Create a new date object in local time
    const localDate = new Date();

    // Get the time zone offset in minutes
    const timezoneOffset = localDate.getTimezoneOffset();

    // Convert to UTC by adding the offset (in milliseconds)
    const utcDate = new Date(localDate.getTime() + timezoneOffset * 60 * 1000);

    return utcDate;
  }

  createUTCDateFromGMT2(dateStringGMT2, forceIsraelTime: boolean = false) {
    // Parse the input date string (assumed to be in GMT+2 timezone)
    const gmtPlus2Date = new Date(dateStringGMT2);

    // Check if the date is valid
    if (isNaN(gmtPlus2Date.getTime())) {
      throw new Error("Invalid date format. Please provide a valid date string.");
    }

    // Get the local timezone offset in minutes
    const localTimezoneOffset = forceIsraelTime ? -120 : gmtPlus2Date.getTimezoneOffset();

    // Calculate the total offset needed (in milliseconds)
    const totalOffsetMs = (localTimezoneOffset) * 60 * 1000;

    // Create a new date by applying the offset
    const utcDate = new Date(gmtPlus2Date.getTime() + totalOffsetMs);

    return utcDate;
  }

  createGMTPlus2Date(utcDateString) {
    // Parse the input UTC date string
    if (utcDateString.includes('+')) {
      utcDateString = utcDateString.split('+')[0];
    }
    const utcDate = new Date(utcDateString);

    // Check if the date string already has UTC offset of 0
    // if (!utcDateString.endsWith('Z') && !utcDateString.endsWith('+00:00') && utcDate.getTimezoneOffset() !== 0) {
    //   return utcDate;
    // }

    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      throw new Error("Invalid date format. Please provide a valid UTC date string.");
    }

    // Calculate the offset from UTC to GMT+2 (2 hours = 120 minutes = 7,200,000 milliseconds)
    const utcToGmtPlus2Offset = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

    // Create a new date by adding the GMT+2 offset to the UTC time
    const gmtPlus2Date = new Date(utcDate.getTime() + utcToGmtPlus2Offset);

    return gmtPlus2Date;
  }

  calculateDiffInMinFromPresentTime(time) {
    const timeToCalculate = new Date(time)
    const currentTime = new Date()
    const diff = (timeToCalculate.getTime() - currentTime.getTime()) / 1000 / 60
    return diff 
  }
}
